import React, { useEffect, useState } from "react";
import { CheckCircle2, Loader2, AlertCircle, MessageSquare } from "lucide-react";
import { getActiveForms, getActiveForm, submitFeedback, FORM_KEY } from "./services/feedbackService";

const DEFAULT_QUESTIONS = [
  { id: "dq1", question_text: "How did the advice we provided on the policy read to you? Accuracy, usefulness, completeness?", question_type: "textarea", is_required: false },
  { id: "dq2", question_text: "How did the website flow for your use? Improvements? Design?", question_type: "textarea", is_required: false },
  { id: "dq3", question_text: "How would you rate the functionality in this platform? Do you need additional functionality?", question_type: "textarea", is_required: false },
  { id: "dq4", question_text: "Other feedback? View on viability or more?", question_type: "textarea", is_required: false },
];

const formatSubmittedAt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${date} · ${time}`;
};

const getQId = (q) => q.id ?? q.question_id;

const getPrefilledAnswer = (q) => {
  const ua = q?.user_answer;
  if (!ua) return undefined;

  const qType = q.question_type ?? q.questionType ?? "textarea";
  if (qType === "multi_select") {
    if (Array.isArray(ua.answer_json)) return ua.answer_json;
    if (typeof ua.answer_text === "string" && ua.answer_text.trim()) return [ua.answer_text.trim()];
    return [];
  }

  if (qType === "rating") {
    const n = Number(ua.answer_text ?? ua.answer_json);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  if (qType === "boolean") {
    const raw = ua.answer_text ?? ua.answer_json;
    if (typeof raw === "boolean") return raw;
    if (typeof raw === "string") {
      if (raw.toLowerCase() === "true") return true;
      if (raw.toLowerCase() === "false") return false;
    }
    return undefined;
  }

  if (typeof ua.answer_text === "string") return ua.answer_text;
  if (Array.isArray(ua.answer_json)) return ua.answer_json.join(", ");
  if (ua.answer_json !== null && ua.answer_json !== undefined) return String(ua.answer_json);
  return "";
};

const RatingInput = ({ value, onChange, disabled }) => (
  <div className="flex items-center gap-2 flex-wrap">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        disabled={disabled}
        onClick={() => onChange(value === n ? 0 : n)}
        className={`w-10 h-10 rounded-lg text-sm font-bold border-2 transition-all ${
          value >= n
            ? "bg-[#5B4FCF] border-[#5B4FCF] text-white shadow-sm"
            : "border-surface-border text-brand-muted hover:border-[#5B4FCF] hover:text-[#5B4FCF]"
        } disabled:opacity-60 disabled:cursor-not-allowed`}
      >
        {n}
      </button>
    ))}
    <span className="text-xs text-brand-muted ml-1">{value > 0 ? `${value} / 5` : "Select a rating"}</span>
  </div>
);

const FeedbackPage = () => {
  const [form, setForm] = useState(null);
  const [formKey, setFormKey] = useState(FORM_KEY);
  const [questions, setQuestions] = useState(DEFAULT_QUESTIONS);
  const [formReady, setFormReady] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [alreadySubmittedMessage, setAlreadySubmittedMessage] = useState("");
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const loadActiveForm = async () => {
      setLoading(true);
      setLoadError("");
      setFormReady(false);
      setAlreadySubmitted(false);
      setSubmission(null);
      setAlreadySubmittedMessage("");

      let selectedForm = null;

      try {
        const formsRes = await getActiveForms({ page: 1, limit: 20, sort_order: "desc" });
        const formsPayload = formsRes?.data ?? formsRes;
        const forms = Array.isArray(formsPayload?.forms) ? formsPayload.forms : [];

        if (forms.length === 0) {
          if (!cancelled) {
            setLoadError("no_active_form");
            setLoading(false);
          }
          return;
        }

        selectedForm = FORM_KEY ? (forms.find((f) => f?.key === FORM_KEY) ?? forms[0]) : forms[0];
      } catch (err) {
        const msg = (err?.message || "").toLowerCase();
        const noActive = err?.status === 400 || err?.status === 404 || msg.includes("no active") || msg.includes("not found");
        if (!cancelled) {
          setLoadError(noActive ? "no_active_form" : (err?.message || "Failed to load feedback form."));
          setLoading(false);
        }
        return;
      }

      if (!selectedForm?.key) {
        if (!cancelled) {
          setLoadError("no_active_form");
          setLoading(false);
        }
        return;
      }

      try {
        const formRes = await getActiveForm(selectedForm.key);
        if (cancelled) return;

        const raw = formRes;
        const payload = raw?.data ?? raw;

        const qs =
          Array.isArray(payload?.version?.questions) ? payload.version.questions :
          Array.isArray(payload?.active_version?.questions) ? payload.active_version.questions :
          Array.isArray(payload?.questions) ? payload.questions :
          Array.isArray(payload?.form?.questions) ? payload.form.questions :
          Array.isArray(raw?.version?.questions) ? raw.version.questions :
          null;

        if (qs && qs.length > 0) {
          setQuestions([...qs].sort((a, b) => (a?.order_no ?? 0) - (b?.order_no ?? 0)));
          setFormReady(true);
        } else if (qs && qs.length === 0) {
          setLoadError("no_questions");
          setFormReady(false);
        } else {
          setLoadError("no_active_form");
          setFormReady(false);
        }

        const formMeta = payload?.form ?? (payload?.id ? payload : null);
        if (formMeta) setForm(formMeta);
        setFormKey(formMeta?.key ?? selectedForm?.key ?? FORM_KEY);

        const cannotSubmit = payload?.can_submit === false;
        const isAlreadySubmitted = payload?.already_submitted === true || cannotSubmit;
        setAlreadySubmitted(isAlreadySubmitted);
        setSubmission(payload?.submission ?? null);

        if (isAlreadySubmitted) {
          setAlreadySubmittedMessage(raw?.message || payload?.message || "You have already submitted feedback for this version");
        }

        const nextAnswers = {};
        (qs || []).forEach((q) => {
          const qId = getQId(q);
          if (!qId) return;
          const prefill = getPrefilledAnswer(q);
          if (prefill !== undefined && prefill !== null && !(typeof prefill === "string" && prefill === "")) {
            nextAnswers[qId] = prefill;
          }
        });
        setAnswers(nextAnswers);

        setLoading(false);
      } catch (err) {
        const msg = (err?.message || "").toLowerCase();
        const isAlready = err?.status === 409 && msg.includes("already submitted");
        const noActive = err?.status === 400 || err?.status === 404 || msg.includes("no active") || msg.includes("not found");

        if (!cancelled) {
          if (isAlready) {
            setAlreadySubmitted(true);
            setAlreadySubmittedMessage(err?.message || "You have already submitted feedback for this version");
          } else if (noActive) {
            setLoadError("no_active_form");
          } else {
            setLoadError(err?.message || "Failed to load feedback form.");
          }
          setLoading(false);
        }
      }
    };

    loadActiveForm();
    return () => { cancelled = true; };
  }, [refreshTick]);

  const handleChange = (id, value) => {
    if (alreadySubmitted) return;
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formReady || alreadySubmitted) {
      setError("Feedback form is not available right now. Please try again later.");
      return;
    }

    const hasAny = questions.some((q) => {
      const v = answers[getQId(q)];
      return v !== undefined && v !== "" && v !== null && v !== 0;
    });
    if (!hasAny) {
      setError("Please answer at least one question before submitting.");
      return;
    }

    const firstMissingRequired = questions.find((q) => {
      if (!q.is_required) return false;
      const value = answers[getQId(q)];
      if (Array.isArray(value)) return value.length === 0;
      return value === undefined || value === null || value === "" || value === 0;
    });
    if (firstMissingRequired) {
      setError("Please answer all required questions before submitting.");
      return;
    }

    const answersList = questions
      .filter((q) => {
        const v = answers[getQId(q)];
        return v !== undefined && v !== "" && v !== null && v !== 0;
      })
      .map((q) => {
        const id = getQId(q);
        const v = answers[id];
        const type = q.question_type ?? q.questionType ?? "textarea";

        if (type === "multi_select") {
          return { question_id: id, answer_json: Array.isArray(v) ? v : [v] };
        }

        return { question_id: id, answer_text: String(v).trim() };
      });

    try {
      setSubmitting(true);
      const resolvedKey = formKey || form?.key || FORM_KEY;
      const submitRes = await submitFeedback({ answers: answersList, source: "web" }, resolvedKey);
      const submitPayload = submitRes?.data ?? submitRes;

      setSubmitted(true);
      if (submitPayload?.already_submitted) {
        setAlreadySubmittedMessage(submitRes?.message || "You have already submitted feedback for this version");
      }

      setRefreshTick((prev) => prev + 1);
    } catch (err) {
      const msg = (err?.message || "").toLowerCase();
      if (err?.status === 409 && msg.includes("already submitted")) {
        setAlreadySubmitted(true);
        setAlreadySubmittedMessage(err?.message || "You have already submitted feedback for this version");
        setError("");
        setRefreshTick((prev) => prev + 1);
      } else if (err?.status === 404 || err?.status === 400) {
        setFormReady(false);
        setLoadError("no_active_form");
      } else {
        setError(err.message || "Failed to submit feedback. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-[#5B4FCF]" />
      </div>
    );
  }

  if (!loading && !formReady) {
    const isNoQuestions = loadError === "no_questions";
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-xl font-bold text-brand-text mb-2">
            {alreadySubmitted ? "Feedback already submitted" : isNoQuestions ? "Form setup incomplete" : "Feedback not available"}
          </h2>
          <p className="text-sm text-brand-muted leading-relaxed">
            {alreadySubmitted
              ? (alreadySubmittedMessage || "You have already submitted feedback for this version")
              : isNoQuestions
              ? "The feedback form was published but has no questions yet. Please contact the admin."
              : loadError && loadError !== "no_active_form"
              ? loadError
              : "No active feedback form available"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 w-full">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 rounded-lg bg-[#ECE9FF] flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-[#5B4FCF]" />
          </div>
          <h1 className="text-2xl font-bold text-brand-text">{form?.title || "Feedback"}</h1>
        </div>
        <p className="text-sm text-brand-muted pl-12">
          {form?.description || "Share your thoughts on the platform. Your feedback helps us improve."}
        </p>

        {alreadySubmitted && (
          <p className="text-xs text-brand-muted pl-12 mt-2">
            {alreadySubmittedMessage || "You have already submitted feedback for this version"}
            {submission?.submitted_at ? ` · Submitted on ${formatSubmittedAt(submission.submitted_at)}` : ""}
          </p>
        )}
      </div>

      {submitted && (
        <div className="mb-4 flex items-center gap-2 text-sm text-success bg-success/10 rounded-lg px-4 py-3 border border-success/20">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          {!alreadySubmitted ? "Thank you for your feedback!" : "Feedback already submitted for this version."}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {questions.map((q, index) => {
          const qId = getQId(q);
          const qText = q.question_text ?? q.questionText ?? "";
          const qType = q.question_type ?? q.questionType ?? "textarea";

          return (
            <div key={qId} className="bg-white rounded-2xl border border-surface-border px-6 py-5 flex gap-5">
              <span className="text-3xl font-extrabold text-[#5B4FCF]/20 leading-none select-none w-6 shrink-0 mt-0.5">{index + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-brand-text mb-3 leading-snug">
                  {qText}
                  {q.is_required && <span className="text-red-500 ml-1">*</span>}
                </p>

                {qType === "rating" ? (
                  <RatingInput value={answers[qId] || 0} onChange={(v) => handleChange(qId, v)} disabled={alreadySubmitted} />
                ) : qType === "boolean" ? (
                  <div className="flex items-center gap-3">
                    {[true, false].map((opt) => (
                      <button
                        key={String(opt)}
                        type="button"
                        disabled={alreadySubmitted}
                        onClick={() => handleChange(qId, opt)}
                        className={`px-5 py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                          answers[qId] === opt
                            ? "bg-[#5B4FCF] border-[#5B4FCF] text-white"
                            : "border-surface-border text-brand-muted hover:border-[#5B4FCF] hover:text-[#5B4FCF]"
                        } disabled:opacity-60 disabled:cursor-not-allowed`}
                      >
                        {opt ? "Yes" : "No"}
                      </button>
                    ))}
                  </div>
                ) : qType === "single_select" ? (
                  <div className="flex flex-wrap gap-2">
                    {(q.options_json ?? []).map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        disabled={alreadySubmitted}
                        onClick={() => handleChange(qId, opt)}
                        className={`px-4 py-1.5 rounded-full text-sm font-medium border-2 transition-all ${
                          answers[qId] === opt
                            ? "bg-[#5B4FCF] border-[#5B4FCF] text-white"
                            : "border-surface-border text-brand-muted hover:border-[#5B4FCF] hover:text-[#5B4FCF]"
                        } disabled:opacity-60 disabled:cursor-not-allowed`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                ) : qType === "multi_select" ? (
                  <div className="flex flex-wrap gap-2">
                    {(q.options_json ?? []).map((opt) => {
                      const selected = Array.isArray(answers[qId]) && answers[qId].includes(opt);
                      const toggle = () => {
                        const prev = Array.isArray(answers[qId]) ? answers[qId] : [];
                        handleChange(qId, selected ? prev.filter((x) => x !== opt) : [...prev, opt]);
                      };

                      return (
                        <button
                          key={opt}
                          type="button"
                          disabled={alreadySubmitted}
                          onClick={toggle}
                          className={`px-4 py-1.5 rounded-full text-sm font-medium border-2 transition-all ${
                            selected
                              ? "bg-[#5B4FCF] border-[#5B4FCF] text-white"
                              : "border-surface-border text-brand-muted hover:border-[#5B4FCF] hover:text-[#5B4FCF]"
                          } disabled:opacity-60 disabled:cursor-not-allowed`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    <textarea
                      rows={3}
                      value={answers[qId] || ""}
                      readOnly={alreadySubmitted}
                      onChange={(e) => handleChange(qId, e.target.value)}
                      placeholder="Your answer..."
                      maxLength={2000}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-surface-border text-sm text-brand-text placeholder-brand-light bg-[#FAFAFA] resize-none focus:outline-none focus:ring-2 focus:ring-[#5B4FCF]/25 focus:border-[#5B4FCF] transition read-only:cursor-default"
                    />
                    <p className="text-right text-[11px] text-brand-light mt-1">{(answers[qId] || "").length}/2000</p>
                  </>
                )}
              </div>
            </div>
          );
        })}

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3 border border-red-100">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {!alreadySubmitted && (
          <div className="flex justify-end pt-1">
            <button
              type="submit"
              disabled={submitting || !formReady}
              className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold bg-[#5B4FCF] text-white hover:bg-[#4a40b8] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? "Submitting..." : "Submit Feedback"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default FeedbackPage;
